import {Field, Formik, FormikHelpers as FormikActions} from 'formik';
import React from 'react';
import {Button} from '~/components/Button';
import {InputType} from '~/components/Controls/Input';
import {
    CheckboxField,
    EditorField,
    Form,
    FormColumn,
    FormControls,
    FormRow,
    FormTitle,
    hasErrors,
    InputField,
    IntentText, MultiInputField,
    LabelWithLink,
    SelectField,
    TagsField
} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {SelectCountryField} from '~/components/SelectCountry';
import {TabsWithBody} from '~/components/Tabs';
import {Intent} from '~/lib/intent';
import {prettyJSON} from '~/lib/json';
import {SelectAccountManagerField} from '~/modules/Clients/components/SelectAccountManager';
import {MultiSelectDataCubesField} from '~/modules/Clients/components/SelectDataCube';
import {SelectIntegrationStatusField} from '~/modules/Clients/components/SelectIntegrationStatus';
import {SelectMediationField} from '~/modules/Clients/components/SelectMediation';
import {SelectPresetField} from '~/modules/Clients/components/SelectPreset';
import {SelectSalesRepresentativeField} from '~/modules/Clients/components/SelectSalesRepresentative';
import {
    accountInfoConditions,
    accountPaymentTypes,
    TakeRateCapType,
    takeRateCapTypeLabels,
    takeRateCapTypes
} from '~/modules/Clients/models/AccountInfo';
import {ISeller, onSubmitPreHandle, sellerRegions, SellerSchema} from '~/modules/Clients/models/Seller';
import {REPORTING_API_URL_HELP} from '~/modules/Clients/models/Agency';
import {dictionaryLinks} from '~/modules/Dictionary/links';
import { SelectClientTypeField } from '../components/SelectClientType';

interface ISellerForm {
    seller?: ISeller;
    onSubmit?: (values: ISeller, formikActions: FormikActions<ISeller>, close: IModalComponentClose) => void;
}

export const SellerForm: IModalComponent<ISellerForm> = props => <Formik initialValues={{
    ...props.seller,
    configuration: prettyJSON(props.seller.configuration)
}}
validationSchema={SellerSchema}
onSubmit={(seller, actions) => props.onSubmit(onSubmitPreHandle(seller), actions, props.close)}
>
    {formik => {
        const {values: seller, status, isSubmitting} = formik;
        return <Form>
            <FormTitle>{seller.id ? 'Edit Seller' : 'Create Seller'}</FormTitle>
            {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
            <TabsWithBody tabs={[{
                title: 'General',
                children: <FormRow noBorder>
                    <FormColumn>
                        <Field name='name'
                            label='Name'
                            options={{
                                placeholder: 'Name',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                        <Field name='active'
                            label='Active'
                            component={CheckboxField}
                        />
                        <Field name='allowResellerLinesAsDirect'
                            label='Allow reseller as direct'
                            component={CheckboxField}
                        />
                        <Field name='accountManagerId'
                            label={
                                <LabelWithLink
                                    label='Account Manager'
                                    to={dictionaryLinks.category('account-managers')}
                                    title='Open dictionary: account managers'
                                    ariaLabel='Open dictionary: account managers'
                                    target="_self"
                                    onClick={props.close}
                                />
                            }
                            options={{
                                placeholder: 'Account Manager',
                                clearable: true,
                                searchable: false,
                                disabled: isSubmitting
                            }}
                            component={SelectAccountManagerField}
                        />
                        <Field name='salesRepresentativeId'
                            label={
                                <LabelWithLink
                                    label='Sales Representative'
                                    to={dictionaryLinks.category('sales-representatives')}
                                    title='Open dictionary: sales representatives'
                                    ariaLabel='Open dictionary: sales representatives'
                                    target="_self"
                                    onClick={props.close}
                                />
                            }
                            options={{
                                placeholder: 'Sales Representative',
                                clearable: true,
                                searchable: false,
                                disabled: isSubmitting
                            }}
                            component={SelectSalesRepresentativeField}
                        />
                        <Field name='expectedUsDau'
                            label='Expected US DAU'
                            type='number'
                            options={{
                                placeholder: 'Expected US DAU',
                                type: InputType.FLOAT,
                                disabled: isSubmitting,
                                min: 0,
                                step: 0.0001,
                            }}
                            component={InputField}
                        />
                        <Field name='expectedUsSpend'
                            label='Expected US Spend Per US DAU'
                            type='number'
                            options={{
                                placeholder: 'Expected US Spend Per US DAU',
                                type: InputType.FLOAT,
                                disabled: isSubmitting,
                                min: 0,
                                step: 0.0001,
                            }}
                            component={InputField}
                        />
                        <Field name='mediationDashboardNames'
                            label='Mediation Dashboard Names'
                            options={{
                                placeholder: 'Mediation Dashboard Names',
                                disabled: isSubmitting,
                            }}
                            component={MultiInputField}
                        />
                        <Field name='companyEmail'
                            label='Company email'
                            options={{
                                placeholder: 'Company email',
                                disabled: isSubmitting,
                            }}
                            component={InputField}
                        />
                    </FormColumn>
                    <FormColumn>
                        <Field name='mediation'
                            label='Mediation'
                            options={{
                                placeholder: 'Mediation',
                                clearable: true,
                                searchable: false,
                                disabled: isSubmitting
                            }}
                            component={SelectMediationField}
                        />
                        <Field name='integrationStatus'
                            label='Integration Status'
                            options={{
                                placeholder: 'Integration Status',
                                clearable: true,
                                searchable: false,
                                disabled: isSubmitting
                            }}
                            component={SelectIntegrationStatusField}
                        />
                        <Field name='clientType'
                            label='Client Type'
                            options={{
                                placeholder: 'Client Type',
                                clearable: true,
                                searchable: false,
                                disabled: isSubmitting
                            }}
                            component={SelectClientTypeField}
                        />
                        <Field name='minPaymentsBalance'
                            label='Minimum Payments Balance'
                            type='number'
                            options={{
                                placeholder: 'Minimum Payments Balance',
                                type: InputType.INTEGER,
                                disabled: isSubmitting,
                                min: 0,
                                step: 1,
                            }}
                            component={InputField}
                        />
                        <Field name='dataCubes'
                            label='Data Cubes'
                            options={{
                                placeholder: 'Data Cubes',
                                clearable: true,
                                searchable: true,
                                disabled: isSubmitting
                            }}
                            component={MultiSelectDataCubesField}
                        />
                        <Field name='presetId'
                            label='Preset'
                            options={{
                                side: 'supply',
                                preselectDefault: true,
                                searchable: true,
                                disabled: isSubmitting
                            }}
                            component={SelectPresetField}
                        />
                        <Field name={'country'}
                            label='Country'
                            options={{
                                placeholder: 'Country',
                                searchable: true,
                                disabled: isSubmitting,
                                clearable: true,
                                short: false
                            }}
                            component={SelectCountryField}
                        />
                        <Field name='region'
                            label='Region'
                            options={{
                                placeholder: 'Region',
                                clearable: true,
                                items: sellerRegions.map(region => ({
                                    value: region,
                                    label: region
                                })),
                                disabled: isSubmitting
                            }}
                            component={SelectField}
                        />
                    </FormColumn>
                </FormRow>
            }, {
                title: 'SSP',
                intent: hasErrors(formik, ['attributes']) && Intent.DANGER,
                children: <FormRow noBorder>
                    <FormColumn>
                        <Field name='attributes.publisherId'
                            label='Publisher ID'
                            options={{
                                placeholder: 'Publisher ID',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                    </FormColumn>
                    <FormColumn>
                        <Field name='attributes.reportingApiUrl'
                            label='Reporting API URL'
                            help={REPORTING_API_URL_HELP}
                            options={{
                                placeholder: 'https://',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                        <Field name='attributes.accessUsername'
                            label='Access Username'
                            options={{
                                placeholder: 'Username',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                        <Field name='attributes.accessPassword'
                            label='Access Password'
                            options={{
                                placeholder: 'Password',
                                type: 'password',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                        <Field name='attributes.accessToken'
                            label='Access Token'
                            options={{
                                placeholder: 'Token',
                                type: 'password',
                                disabled: isSubmitting
                            }}
                            component={InputField}
                        />
                    </FormColumn>
                </FormRow>
            }, {
                title: 'Payment',
                intent: hasErrors(formik, ['conditions', 'paymentType', 'takeRateCap']) && Intent.DANGER,
                children: <FormRow noBorder>
                    <FormColumn>
                        <Field name='conditions'
                            label='Conditions'
                            options={{
                                placeholder: 'Conditions',
                                items: accountInfoConditions.map(condition => ({
                                    value: condition,
                                    label: condition
                                })),
                                disabled: isSubmitting
                            }}
                            component={SelectField}
                        />
                        <Field name='paymentType'
                            label='Payment Type'
                            options={{
                                placeholder: 'Payment Types',
                                items: accountPaymentTypes.map(type => ({value: type, label: type})),
                                disabled: isSubmitting
                            }}
                            component={SelectField}
                        />
                    </FormColumn>
                    <FormColumn>
                        <Field name='takeRateCap.type'
                            label='Take rate cap type'
                            options={{
                                placeholder: 'Take rate cap type',
                                items: takeRateCapTypes.map(type => ({
                                    value: type,
                                    label: takeRateCapTypeLabels[type]
                                })),
                                disabled: isSubmitting
                            }}
                            component={SelectField}
                        />
                        {seller.takeRateCap.type && seller.takeRateCap.type !== TakeRateCapType.DISABLED && (
                            <Field name='takeRateCap.value'
                                label='Take rate cap'
                                type='number'
                                options={{
                                    placeholder: 'Take rate cap',
                                    type: InputType.FLOAT,
                                    disabled: isSubmitting,
                                    min: 0,
                                    step: 0.0001
                                }}
                                component={InputField}
                            />
                        )}
                    </FormColumn>
                </FormRow>
            }, {
                title: 'Blockers',
                intent: hasErrors(formik, ['bcat', 'bapp', 'badv']) && Intent.DANGER,
                children: <FormRow noBorder>
                    <FormColumn>
                        <Field name='bcat'
                            label='Categories'
                            options={{
                                placeholder: 'Categories',
                                disabled: isSubmitting
                            }}
                            component={TagsField}
                        />
                        <Field name='bapp'
                            label='Applications'
                            options={{
                                placeholder: 'Applications',
                                disabled: isSubmitting
                            }}
                            component={TagsField}
                        />
                        <Field name='badv'
                            label='Advertising Domains'
                            options={{
                                placeholder: 'Advertising Domains',
                                disabled: isSubmitting
                            }}
                            component={TagsField}
                        />
                    </FormColumn>
                </FormRow>
            }, {
                title: 'Configuration',
                children: <FormRow noBorder>
                    <FormColumn>
                        <Field name='configuration'
                            options={{
                                disabled: isSubmitting,
                                mode: 'json',
                                canFullScreen: true
                            }}
                            component={EditorField}
                        />
                    </FormColumn>
                </FormRow>
            }]}/>
            <FormControls>
                <Button disabled={isSubmitting}
                    onClick={props.close}
                >Cancel</Button>
                <Button type='submit'
                    disabled={isSubmitting}
                    intent={Intent.PRIMARY}
                    rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                >Save</Button>
            </FormControls>
        </Form>;
    }}
</Formik>;
